import React, { useCallback, useEffect, useState } from 'react';
import * as api from '../../billMemory/api';
import { shortDate } from '../../billMemory/core';
import HealthView from './HealthView';
import ReadyList from './ReadyList';
import AuditView from './AuditView';

const pct = (x) => (x == null ? '—' : `${Math.round(x * 1000) / 10}%`);
const TRI = ['off', 'shadow', 'on'];

/** "Automatic: Shadow since 22 Oct" — the business as a whole; each source's own numbers follow (Phase 1 minor 4). */
function autopilotLine(a) {
  return `Automatic: ${a.state === 'on' ? 'On' : 'Shadow'} since ${shortDate(a.state_since)}`;
}

/**
 * One source, by Fix 1's bars: "vendor lessons: Shadow since 22 Oct — 12/30 greens · 5/7 days · 0 wrong", or once
 * On "dictionary: On since 25 Oct — 40 live greens · 1 wrong in the last 50".
 */
export function sourceNumbersLine(s) {
  const n = s.numbers || {};
  if (s.state === 'on') {
    return `${s.words}: On since ${shortDate(s.live_since || s.state_since)} — ${n.liveGreens || 0} live greens · ${n.liveWindowWrong || 0} wrong in the last 50`;
  }
  const state = s.state === 'ready' ? 'Ready' : 'Shadow';
  return `${s.words}: ${state} since ${shortDate(s.state_since)} — ${n.greens || 0}/30 greens · ${Math.floor(n.days || 0)}/7 days · ${n.windowWrong || 0} wrong`;
}

/**
 * Invexis admin (design §16): every switch for this business, the global master and AI-cost
 * switches, "run import", dictionary version status (only a reviewed version may go to Shadow),
 * and the rollout measures. The server refuses all of this to anyone else.
 */
export default function AdminPanel({ businessId }) {
  const [d, setD] = useState(null);
  const [msg, setMsg] = useState('');
  const load = useCallback(() => api.getAdminBusiness(businessId).then(setD).catch(() => setMsg('Could not load the admin view.')), [businessId]);
  useEffect(() => { load(); }, [load]);
  const run = async (fn, done) => {
    setMsg('');
    try { await fn(); setMsg(done); await load(); } catch (e) { setMsg((e && e.message) || 'That was refused.'); }
  };
  if (!d) return <section className="bm-panel"><h2>Invexis admin</h2><div className="bm-note">{msg || 'Loading…'}</div></section>;
  const s = d.switches || {};
  const m = d.measures || {};
  const sel = (key, values) => (
    <label className="bm-field">{key.replace('_', ' ')}
      <select className="bm-select" value={s[key] || 'off'} onChange={(e) => run(() => api.putSwitches(businessId, { [key]: e.target.value }), 'Saved.')}>
        {values.map((v) => <option key={v} value={v}>{v}</option>)}
      </select>
    </label>
  );
  const own = d.source === 'own';
  return (
    <section className="bm-panel">
      <h2>Invexis admin</h2>
      <div className="bm-note">
        <span>{own ? 'Own setting' : 'Following the default'}</span>
        {own && (
          <>
            {' '}
            <button type="button" className="irr-secondary-btn" onClick={() => run(() => api.backToDefault(businessId), 'Back to the defaults.')}>
              Back to default
            </button>
          </>
        )}
      </div>
      {!own && d.autopilot && <div className="bm-note">{autopilotLine(d.autopilot)}</div>}
      {!own && (d.sources || []).map((s) => <div key={s.source} className="bm-note">{sourceNumbersLine(s)}</div>)}
      <div className="bm-filters">
        {sel('mode', TRI)}
        {sel('vendor_rule', TRI)}
        {sel('dictionary', TRI)}
        {sel('screen', ['off', 'on'])}
        <label className="bm-field">dictionary version
          <select className="bm-select" value={s.dictionary_version || ''} onChange={(e) => run(() => api.putSwitches(businessId, { dictionary_version: e.target.value ? Number(e.target.value) : null }), 'Saved.')}>
            <option value="">newest live</option>
            {(d.dictionary_versions || []).map((v) => <option key={v.version} value={v.version}>{`v${v.version} (${v.status})`}</option>)}
          </select>
        </label>
      </div>
      <div className="bm-filters">
        <label className="bm-field">master (every client)
          <input type="checkbox" checked={!!(d.global && d.global.master)} onChange={(e) => run(() => api.putGlobal({ master: e.target.checked }), 'Saved.')} />
        </label>
        <label className="bm-field">AI cost changes (global)
          <select className="bm-select" value={(d.global && d.global.ai_cost) || 'off'} onChange={(e) => run(() => api.putGlobal({ ai_cost: e.target.value }), 'Saved.')}>
            <option value="off">off</option><option value="on">on</option>
          </select>
        </label>
        <button type="button" className="irr-secondary-btn" onClick={() => run(() => api.runImport(businessId), 'Import done.')}>
          {d.imported_at ? 'Run import again' : 'Run import'}
        </button>
      </div>
      <table className="irr-qty-table bm-table">
        <thead><tr><th>Dictionary version</th><th>Status</th><th>Reviewed by</th><th>Set status</th></tr></thead>
        <tbody>
          {(d.dictionary_versions || []).map((v) => (
            <tr key={v.version}>
              <td>{`v${v.version}`}</td><td>{v.status}</td><td>{v.reviewed_by || '—'}</td>
              <td>
                <select className="bm-select" value="" onChange={(e) => e.target.value && run(() => api.putDictStatus(v.version, e.target.value), 'Saved.')}>
                  <option value="">change…</option>
                  {['reviewed', 'shadow', 'live', 'retired'].map((x) => <option key={x} value={x}>{x}</option>)}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="bm-count">
        {`Lines ${m.lines || 0} · green ${pct(m.greenRate)} · Shadow agreement ${pct(m.shadowAgreement)} · wrong greens ${m.wrongGreens || 0} (${pct(m.wrongGreenRate)}) · repeat reds ${m.repeatReds || 0} · failures ${m.memoryFailures || 0}`}
      </div>
      <div className="bm-note">
        {`Wrong greens by source: ${Object.entries(m.wrongGreensBySource || {}).map(([k, n]) => `${k.replace('_', ' ')} ${n}`).join(', ') || '—'}`}
      </div>
      <div className="bm-note">{`Taps per bill: ${(m.tapsPerBill || []).join(', ') || '—'}`}</div>
      {msg && <div className="bm-note">{msg}</div>}
      {/* Fix plan, Monitoring: every business at once — what waits for approval, and how each is doing */}
      <ReadyList />
      <AuditView />
      <HealthView />
    </section>
  );
}
